import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Arrow from '../../components/ui/Arrow';
import BlurText from '../../components/effects/BlurText';
import SpotlightCard from '../../components/effects/SpotlightCard';
import { localizedPath, useLocale, useMessages } from '../../i18n/locale';
import NotFoundPage from './NotFoundPage';

const C_HELLO_WORLD_LINES = [
  [
    { text: '#include ', type: 'preprocessor' },
    { text: '<stdio.h>', type: 'header' },
  ],
  [],
  [
    { text: 'int ', type: 'type' },
    { text: 'main', type: 'function' },
    { text: '(', type: 'punctuation' },
    { text: 'void', type: 'type' },
    { text: ') {', type: 'punctuation' },
  ],
  [
    { text: '    ' },
    { text: 'printf', type: 'function' },
    { text: '(', type: 'punctuation' },
    { text: '"Hello, world!\\n"', type: 'string' },
    { text: ');', type: 'punctuation' },
  ],
  [
    { text: '    ' },
    { text: 'return', type: 'keyword' },
    { text: ' ', type: 'keyword' },
    { text: '0', type: 'number' },
    { text: ';', type: 'punctuation' },
  ],
  [{ text: '}', type: 'punctuation' }],
];

const C_HELLO_WORLD_LENGTH =
  C_HELLO_WORLD_LINES.reduce(
    (total, line) => total + line.map((token) => token.text).join('').length,
    0,
  ) +
  C_HELLO_WORLD_LINES.length -
  1;

function AnimatedCHelloWorld() {
  const [visibleCharacters, setVisibleCharacters] = useState(0);

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setVisibleCharacters(C_HELLO_WORLD_LENGTH);
      return undefined;
    }

    let characterIndex = 0;
    let direction = 1;
    let typingInterval;
    let startTimeout;
    let pauseTimeout;

    const startTyping = () => {
      typingInterval = window.setInterval(() => {
        characterIndex += direction;
        setVisibleCharacters(characterIndex);

        if (direction === 1 && characterIndex >= C_HELLO_WORLD_LENGTH) {
          window.clearInterval(typingInterval);
          pauseTimeout = window.setTimeout(() => {
            direction = -1;
            startTyping();
          }, 2400);
        } else if (direction === -1 && characterIndex <= 0) {
          window.clearInterval(typingInterval);
          pauseTimeout = window.setTimeout(() => {
            direction = 1;
            startTyping();
          }, 500);
        }
      }, 48);
    };

    startTimeout = window.setTimeout(startTyping, 450);
    return () => {
      window.clearTimeout(startTimeout);
      window.clearTimeout(pauseTimeout);
      window.clearInterval(typingInterval);
    };
  }, []);

  let lineStart = 0;

  return (
    <pre className="oss-code-editor" aria-hidden="true">
      <code>
        {C_HELLO_WORLD_LINES.map((line, index) => {
          const lineText = line.map((token) => token.text).join('');
          const visibleInLine = Math.min(
            lineText.length,
            Math.max(0, visibleCharacters - lineStart),
          );
          const isCursorLine =
            (visibleCharacters >= lineStart && visibleCharacters <= lineStart + lineText.length) ||
            (index === C_HELLO_WORLD_LINES.length - 1 &&
              visibleCharacters === C_HELLO_WORLD_LENGTH);
          let tokenStart = 0;

          const tokens = line.map((token, tokenIndex) => {
            const visibleLength = Math.max(
              0,
              Math.min(token.text.length, visibleInLine - tokenStart),
            );
            tokenStart += token.text.length;
            if (visibleLength === 0) return null;

            return (
              <span
                className={token.type ? `oss-code-token is-${token.type}` : undefined}
                key={`${index}-${tokenIndex}`}
              >
                {token.text.slice(0, visibleLength)}
              </span>
            );
          });

          const lineElement = (
            <span className="oss-code-line" key={index}>
              <span className="oss-code-line__number">{String(index + 1).padStart(2, '0')}</span>
              {tokens}
              {isCursorLine && <span className="oss-code-cursor" />}
            </span>
          );

          lineStart += lineText.length + 1;
          return lineElement;
        })}
      </code>
    </pre>
  );
}

function Breadcrumb({ copy, locale, detail = false }) {
  return (
    <nav className="oss-breadcrumb" aria-label={copy.breadcrumbLabel}>
      <Link to={localizedPath(locale)}>{copy.home}</Link>
      <span aria-hidden="true">/</span>
      {detail ? (
        <Link to={localizedPath(locale, '/open-source')}>{copy.name}</Link>
      ) : (
        <span>{copy.name}</span>
      )}
    </nav>
  );
}

function RepositoryMark() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M9 19c-4.3 1.4-4.3-2.5-6-3m12 6v-3.9a3.4 3.4 0 0 0-.9-2.6c3-.3 6.1-1.5 6.1-6.7a5.2 5.2 0 0 0-1.4-3.6 4.8 4.8 0 0 0-.1-3.6s-1.2-.4-3.7 1.4a13 13 0 0 0-6.8 0C5.7 1.2 4.5 1.6 4.5 1.6a4.8 4.8 0 0 0-.1 3.6A5.2 5.2 0 0 0 3 8.8c0 5.2 3.1 6.4 6.1 6.7a3.4 3.4 0 0 0-.9 2.6V22" />
    </svg>
  );
}

function ProjectStatus({ project }) {
  return (
    <span className={`oss-status oss-status--${project.statusKey}`}>
      <span aria-hidden="true" />
      {project.status}
    </span>
  );
}

function ProjectList({ copy, locale }) {
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const projects = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return copy.projects.filter((project) => {
      const matchesFilter = filter === 'all' || project.statusKey === filter;
      const searchable = [project.name, project.description, ...project.tags]
        .join(' ')
        .toLowerCase();
      return matchesFilter && (!normalizedQuery || searchable.includes(normalizedQuery));
    });
  }, [copy.projects, filter, query]);

  return (
    <section id="projects" className="oss-catalog">
      <div className="container">
        <header className="oss-catalog__heading">
          <div>
            <h2>{copy.catalog.title}</h2>
            <p>{copy.catalog.description}</p>
          </div>
        </header>

        <div className="oss-toolbar">
          <div className="oss-filters" role="group" aria-label={copy.catalog.filterLabel}>
            {copy.catalog.filters.map((item) => (
              <button
                key={item.key}
                type="button"
                className={filter === item.key ? 'is-active' : ''}
                aria-pressed={filter === item.key}
                onClick={() => setFilter(item.key)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <label className="oss-search">
            <span aria-hidden="true">⌕</span>
            <span className="visually-hidden">{copy.catalog.searchLabel}</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={copy.catalog.searchPlaceholder}
            />
          </label>
        </div>

        <div className="oss-project-list">
          <div className="oss-project-list__header" aria-hidden="true">
            <span>{copy.catalog.projectColumn}</span>
            <span>{copy.catalog.activityColumn}</span>
            <span>{copy.catalog.updatedColumn}</span>
          </div>
          {projects.map((project, index) => (
            <Link
              className="oss-project-row"
              key={project.slug}
              to={localizedPath(locale, `/open-source/${project.slug}`)}
              style={{ '--row-index': index }}
            >
              <div className="oss-project-row__identity">
                <span className="oss-project-row__mark" aria-hidden="true">
                  <RepositoryMark />
                </span>
                <div>
                  <div className="oss-project-row__title">
                    <h3>{project.name}</h3>
                    <ProjectStatus project={project} />
                  </div>
                  <p>{project.description}</p>
                  <div className="oss-project-row__tags">
                    {project.tags.map((tag) => (
                      <span key={tag}>{tag}</span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="oss-project-row__activity">
                <span
                  className="oss-language-dot"
                  style={{ '--language-color': project.languageColor }}
                />
                <span>{project.language}</span>
                <span className="oss-project-row__separator">·</span>
                <span>{project.license}</span>
              </div>
              <div className="oss-project-row__updated">
                <span>{project.updated}</span>
                <span className="oss-project-row__arrow" aria-hidden="true">
                  ↗
                </span>
              </div>
            </Link>
          ))}
          {projects.length === 0 && <p className="oss-empty">{copy.catalog.empty}</p>}
        </div>
        <p className="oss-catalog__note">{copy.catalog.note}</p>
      </div>
    </section>
  );
}

function ProjectDetail({ copy, locale, project }) {
  const [activeTab, setActiveTab] = useState('overview');
  const tabRefs = useRef({});
  const tabs = [
    { key: 'overview', label: copy.detail.tabs.overview },
    { key: 'license', label: copy.detail.tabs.license },
    { key: 'releases', label: copy.detail.tabs.releases },
    { key: 'roadmap', label: copy.detail.tabs.roadmap },
    { key: 'faq', label: copy.detail.tabs.faq },
  ];
  const panelId = `oss-panel-${project.slug}-${activeTab}`;

  const handleTabKeyDown = (event, index) => {
    let nextIndex = index;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') nextIndex = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = tabs.length - 1;
    else return;
    event.preventDefault();
    const nextTab = tabs[nextIndex];
    setActiveTab(nextTab.key);
    tabRefs.current[nextTab.key]?.focus();
  };

  return (
    <>
      <section className="oss-detail-hero">
        <div className="container">
          <Breadcrumb copy={copy} locale={locale} detail />
          <div className="oss-detail-hero__body">
            <div>
              <ProjectStatus project={project} />
              <h1>{project.name}</h1>
              <p>{project.description}</p>
              <div className="oss-detail-hero__tags">
                {project.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="oss-detail-hero__actions">
              {project.repositories.length > 0 ? (
                <a className="oss-repository-button" href="#repositories">
                  <RepositoryMark /> {copy.detail.viewRepositories} <Arrow />
                </a>
              ) : (
                <span className="oss-repository-button is-pending">
                  <RepositoryMark /> {copy.detail.repositoryPending}
                </span>
              )}
              <Link className="oss-back-link" to={localizedPath(locale, '/open-source')}>
                {copy.detail.backToList} <Arrow />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="oss-detail-content">
        <div className="container oss-detail-layout">
          <main className="oss-readme">
            <div className="oss-detail-tabs" role="tablist" aria-label={copy.detail.tabLabel}>
              {tabs.map((tab, index) => (
                <button
                  aria-controls={`oss-panel-${project.slug}-${tab.key}`}
                  aria-selected={activeTab === tab.key}
                  className={activeTab === tab.key ? 'is-active' : ''}
                  id={`oss-tab-${project.slug}-${tab.key}`}
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                  ref={(node) => {
                    tabRefs.current[tab.key] = node;
                  }}
                  role="tab"
                  tabIndex={activeTab === tab.key ? 0 : -1}
                  type="button"
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <section
              aria-labelledby={`oss-tab-${project.slug}-${activeTab}`}
              className="oss-tab-panel"
              id={panelId}
              role="tabpanel"
              tabIndex={0}
            >
              {activeTab === 'overview' && (
                <div className="oss-readme__body">
                  <span className="oss-overline">{copy.detail.projectOverview}</span>
                  <h2>{project.readme.title}</h2>
                  <p>{project.readme.description}</p>
                  <h3>{copy.detail.highlights}</h3>
                  <ul>
                    {project.readme.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
              {activeTab === 'license' && (
                <div className="oss-readme__body oss-license-panel">
                  <span className="oss-overline">{copy.detail.license}</span>
                  <h2>{project.license}</h2>
                  <p>{project.licenseContent || copy.detail.licensePending}</p>
                </div>
              )}
              {activeTab === 'releases' && (
                <section className="oss-timeline">
                  <div className="oss-detail-section-heading">
                    <span className="oss-overline">{copy.detail.releaseEyebrow}</span>
                    <h2>{copy.detail.releases}</h2>
                  </div>
                  {project.releases.map((release) => (
                    <article className="oss-release" key={release.version}>
                      <span className="oss-release__marker" aria-hidden="true" />
                      <div className="oss-release__meta">
                        <strong>{release.version}</strong>
                        <span>{release.date}</span>
                      </div>
                      <h3>{release.title}</h3>
                      <p>{release.description}</p>
                    </article>
                  ))}
                </section>
              )}
              {activeTab === 'roadmap' && (
                <section className="oss-tab-list-panel">
                  <span className="oss-overline">{copy.detail.roadmap}</span>
                  <h2>{copy.detail.tabs.roadmap}</h2>
                  <ul className="oss-roadmap">
                    {project.roadmap.map((item) => (
                      <li className={item.done ? 'is-done' : ''} key={item.title}>
                        <span aria-hidden="true">{item.done ? '✓' : '○'}</span>
                        {item.title}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
              {activeTab === 'faq' && (
                <section className="oss-tab-list-panel oss-faq-panel">
                  <span className="oss-overline">{copy.detail.faqEyebrow}</span>
                  <h2>{copy.detail.tabs.faq}</h2>
                  {copy.detail.faqItems.map((item) => (
                    <details key={item.question}>
                      <summary>{item.question}</summary>
                      <p>{item.answer}</p>
                    </details>
                  ))}
                </section>
              )}
            </section>
          </main>

          <aside className="oss-detail-aside">
            <div className="oss-aside-block oss-project-info">
              <span className="oss-overline">{copy.detail.projectInfo}</span>
              <dl>
                <div>
                  <dt>{copy.detail.version}</dt>
                  <dd>{project.version}</dd>
                </div>
                <div>
                  <dt>{copy.detail.license}</dt>
                  <dd>{project.license}</dd>
                </div>
                <div>
                  <dt>{copy.detail.language}</dt>
                  <dd>{project.language}</dd>
                </div>
                <div>
                  <dt>{copy.detail.lastUpdate}</dt>
                  <dd>{project.updated}</dd>
                </div>
              </dl>
            </div>
            <div id="repositories" className="oss-aside-block oss-repositories-block">
              <span className="oss-overline">{copy.detail.repositories}</span>
              {project.repositories.length > 0 ? (
                <div className="oss-repository-list">
                  {project.repositories.map((repository) => (
                    <a
                      className="oss-repository-item"
                      href={repository.url}
                      key={`${repository.platform}-${repository.name}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <span className="oss-repository-item__icon" aria-hidden="true">
                        <RepositoryMark />
                      </span>
                      <span className="oss-repository-item__body">
                        <span className="oss-repository-item__type">
                          {repository.platform} <i>·</i> {repository.kind}
                        </span>
                        <strong>{repository.name}</strong>
                        {repository.description && <small>{repository.description}</small>}
                      </span>
                      <Arrow up />
                    </a>
                  ))}
                </div>
              ) : (
                <p>{copy.detail.repositoryNote}</p>
              )}
            </div>
            <div className="oss-aside-block oss-docs-block">
              <span className="oss-overline">{copy.detail.documentation}</span>
              <p>{copy.detail.documentationNote}</p>
              {project.docsUrl ? (
                <a href={project.docsUrl} target="_blank" rel="noreferrer">
                  {copy.detail.openDocumentation} <Arrow up />
                </a>
              ) : (
                <Link to={localizedPath(locale, `/open-source/${project.slug}/docs`)}>
                  {copy.detail.openDocumentation} <Arrow />
                </Link>
              )}
            </div>
            <div className="oss-aside-block oss-aside-block--activity">
              <span className="oss-overline">{copy.detail.activity}</span>
              <ProjectStatus project={project} />
              <p>{project.activity}</p>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}

function ProjectDocsPage({ copy, locale, project }) {
  return (
    <section className="oss-docs-page">
      <div className="container">
        <Breadcrumb copy={copy} locale={locale} detail />
        <span className="oss-overline">{copy.detail.documentation}</span>
        <h1>
          {project.name}
          <br />
          <span>{copy.detail.tabs.documentation}</span>
        </h1>
        <p>{copy.detail.documentationPlaceholder}</p>
        <Link
          className="oss-repository-button"
          to={localizedPath(locale, `/open-source/${project.slug}`)}
        >
          {copy.detail.backToProject} <Arrow />
        </Link>
      </div>
    </section>
  );
}

export default function OpenSourcePage({ view = 'catalog' }) {
  const locale = useLocale();
  const copy = useMessages().openSourcePage;
  const { projectSlug } = useParams();
  const project = projectSlug ? copy.projects.find((item) => item.slug === projectSlug) : null;

  if (projectSlug && !project) return <NotFoundPage />;
  if (project && view === 'docs') {
    return (
      <main className="oss-page">
        <ProjectDocsPage copy={copy} locale={locale} project={project} />
      </main>
    );
  }

  return (
    <main className="oss-page">
      {project ? (
        <ProjectDetail copy={copy} locale={locale} project={project} />
      ) : (
        <>
          <section className="oss-hero">
            <div className="oss-hero__grid" aria-hidden="true" />
            <div className="container oss-hero__inner">
              <Breadcrumb copy={copy} locale={locale} />
              <div className="oss-hero__layout">
                <div className="oss-hero__copy">
                  <h1>
                    <BlurText
                      className="oss-hero__title-line"
                      text={copy.hero.title[0]}
                      delay={40}
                    />
                    <br />
                    <span className="oss-hero__title-line--muted">
                      <BlurText
                        className="oss-hero__title-line"
                        text={copy.hero.title[1]}
                        delay={180}
                      />
                    </span>
                  </h1>
                  <p>{copy.hero.description}</p>
                  <a className="oss-hero__cta" href="#projects">
                    {copy.hero.cta}
                    <Arrow />
                  </a>
                </div>
                <div className="oss-hero__visual" aria-hidden="true">
                  <div className="oss-orbit oss-orbit--outer" />
                  <div className="oss-orbit oss-orbit--inner" />
                  <div className="oss-orbit__node oss-orbit__node--one" />
                  <div className="oss-orbit__node oss-orbit__node--two" />
                  <SpotlightCard
                    className="oss-terminal-spotlight"
                    spotlightColor="rgba(255, 255, 255, 0.14)"
                  >
                    <div className="oss-hero__terminal">
                      <div className="oss-terminal__top">
                        <span className="oss-terminal__traffic-light is-close" />
                        <span className="oss-terminal__traffic-light is-minimize" />
                        <span className="oss-terminal__traffic-light is-expand" />
                        <b>hello_world.c</b>
                      </div>
                      <AnimatedCHelloWorld />
                    </div>
                  </SpotlightCard>
                </div>
              </div>
            </div>
          </section>
          <ProjectList copy={copy} locale={locale} />
          <section className="oss-invite">
            <div className="container oss-invite__inner">
              <div>
                <span className="oss-overline">{copy.invite.eyebrow}</span>
                <h2>{copy.invite.title}</h2>
              </div>
              <p>{copy.invite.description}</p>
              <Link to={localizedPath(locale, '/community')}>
                {copy.invite.action}
                <Arrow up />
              </Link>
            </div>
          </section>
        </>
      )}
    </main>
  );
}
